/* VIMZO CREATIONS – engraving renderer
   Draws the personalization (text, icon, letter graphic, uploaded design) as a
   grey (#A6A6A6) debossed engraving, then maps it onto the tumbler body's UV
   texture so it wraps and rotates with the 3D model.

   UV mapping of the supplied VIMZO 40 oz models (Body mesh, material "PowderCoat"):
   - u = angle around the body / 360°, front-centre (facing the default camera) = 0, increasing toward the handle side
   - v rises linearly up the straight upper body: 0.781 at the taper, 0.990 under the steel band
   - glTF textures use flipY = false, so higher on the tumbler = lower in the image
   The printable window used here is ±47° around the front, 15–25.6 cm up the body.
   For a different model, only cfg.engrave (uMin/uMax/vBottom/vTop/widthCm/heightCm) needs changing. */
(function () {
  'use strict';

  var FONTS = {
    'oswald': { family: 'Oswald', weight: 600, style: 'normal', spacing: 0.02 },
    'bebas': { family: 'Bebas Neue', weight: 400, style: 'normal', spacing: 0.04 },
    'graduate': { family: 'Graduate', weight: 400, style: 'normal', spacing: 0 },
    'roboto-slab': { family: 'Roboto Slab', weight: 600, style: 'normal', spacing: 0 },
    'libre-baskerville': { family: 'Libre Baskerville', weight: 700, style: 'normal', spacing: 0 },
    'montserrat-italic': { family: 'Montserrat', weight: 600, style: 'italic', spacing: 0 },
    'allura': { family: 'Allura', weight: 400, style: 'normal', spacing: 0 },
    'dancing-script': { family: 'Dancing Script', weight: 600, style: 'normal', spacing: 0 },
    'yellowtail': { family: 'Yellowtail', weight: 400, style: 'normal', spacing: 0 }
  };
  // Right-to-left scripts: engraved with a script font; Latin letters typed alongside keep the chosen font.
  // ratio = text size vs printable height, thick = line height (Nastaliq needs extra room)
  var SCRIPTS = {
    ar: { family: 'Noto Naskh Arabic', weight: 600, ratio: 0.3, thick: 1.6 },
    ur: { family: 'Noto Nastaliq Urdu', weight: 600, ratio: 0.26, thick: 2.0 }
  };
  // Font used for the engraved text in a given language
  function textFont(fontKey, lang) {
    var latin = FONTS[fontKey] || FONTS.oswald;
    var s = SCRIPTS[lang];
    if (s) return { families: [s.family, latin.family], weight: s.weight, style: 'normal', spacing: 0, rtl: true, ratio: s.ratio, thick: s.thick };
    return { families: [latin.family, SCRIPTS.ar.family], weight: latin.weight, style: latin.style, spacing: latin.spacing, rtl: false, thick: 1.2 };
  }
  var LETTER_FONTS = {
    serif: { family: 'Libre Baskerville', weight: 700, style: 'normal' },
    script: { family: 'Allura', weight: 400, style: 'normal' }
  };

  function canvas(w, h) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }
  function fontString(f, px) {
    var families = f.families || [f.family];
    return f.style + ' ' + f.weight + ' ' + Math.max(1, px).toFixed(1) + 'px "' + families.join('", "') + '"';
  }
  function loadImage(src, cors) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      if (cors) img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
  }
  function toWhiteMask(img, size) {
    var w = img.naturalWidth || img.width || size;
    var h = img.naturalHeight || img.height || size;
    var s = size / Math.max(w, h);
    var c = canvas(w * s, h * s);
    var x = c.getContext('2d');
    x.drawImage(img, 0, 0, c.width, c.height);
    x.globalCompositeOperation = 'source-in';
    x.fillStyle = '#fff';
    x.fillRect(0, 0, c.width, c.height);
    return c;
  }

  /* ---------- Asset preparation (cached) ---------- */
  var cache = {};

  // Built-in SVG icon (uses currentColor) or a merchant icon image → white mask
  function iconMask(icon) {
    if (!icon) return Promise.resolve(null);
    var key = 'icon:' + icon.key;
    if (cache[key]) return cache[key];
    var p;
    if (icon.type === 'image' && icon.src) {
      p = loadImage(icon.src, true).then(function (img) { return toWhiteMask(img, 256); });
    } else if (icon.svg) {
      var svg = icon.svg.cloneNode(true);
      svg.setAttribute('width', '256');
      svg.setAttribute('height', '256');
      svg.setAttribute('color', '#ffffff');
      svg.setAttribute('style', 'color:#ffffff');
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      var url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svg));
      p = loadImage(url).then(function (img) { return toWhiteMask(img, 256); });
    } else {
      p = Promise.resolve(null);
    }
    cache[key] = p.catch(function () { return null; });
    return cache[key];
  }

  // Uploaded design → single-colour engraving mask (white where engraved).
  // Dark pixels are engraved; light and transparent pixels are removed. The threshold
  // adapts to the image, so it works for line art and for photos (which become simple shapes).
  function artMask(url) {
    if (!url) return Promise.resolve(null);
    var key = 'art:' + url;
    if (cache[key]) return cache[key];
    cache[key] = loadImage(url).then(function (img) {
      var max = 1200;
      var nw = img.naturalWidth || max;
      var nh = img.naturalHeight || max;
      var s = Math.min(1, max / Math.max(nw, nh));
      var c = canvas(nw * s, nh * s);
      var x = c.getContext('2d');
      x.drawImage(img, 0, 0, c.width, c.height);
      var data = x.getImageData(0, 0, c.width, c.height);
      var p = data.data;
      var sum = 0, n = 0, i;
      for (i = 0; i < p.length; i += 4) {
        if (p[i + 3] > 20) { sum += 0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2]; n++; }
      }
      var threshold = n ? Math.min(170, sum / n) : 128;
      for (i = 0; i < p.length; i += 4) {
        var lum = 0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2];
        var on = p[i + 3] > 20 && lum < threshold;
        p[i] = p[i + 1] = p[i + 2] = 255;
        p[i + 3] = on ? 255 : 0;
      }
      x.putImageData(data, 0, 0);
      return { canvas: c, aspect: c.width / c.height };
    }).catch(function () { return null; });
    return cache[key];
  }
  function ensureFonts(spec) {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    var loads = [document.fonts.load(fontString(textFont(spec.font, spec.lang), 40), spec.text || 'Aa')];
    if (spec.letter) {
      var lf = LETTER_FONTS[spec.letterStyle] || FONTS[spec.font] || FONTS.oswald;
      loads.push(document.fonts.load(fontString(lf, 40), spec.letter));
    }
    return Promise.all(loads).catch(function () {});
  }

  /* ---------- Layout + drawing (design space, white mask) ---------- */
  // spec: { W, H, text, placeholder, font, ratio, orient, icon:{mask}, letter, letterStyle, letterFrame,
  //         art:{canvas, aspect}, scale, pos:{x,y} }
  function composeMask(spec) {
    var W = spec.W, H = spec.H;
    var c = canvas(W, H);
    var x = c.getContext('2d');
    var f = textFont(spec.font, spec.lang);
    var rtl = f.rtl;
    var vertical = spec.orient === 'vertical';
    var hasArt = !!spec.art, hasLetter = !!spec.letter, hasIcon = !!(spec.icon && spec.icon.mask);
    var text = spec.text || '';
    var hasText = !!text && !spec.hideText;
    var rot = (((spec.artRot || 0) % 360) + 360) % 360;
    var artAspect = hasArt ? (rot % 180 ? 1 / spec.art.aspect : spec.art.aspect) : 1;
    var artOnly = hasArt && !hasText && !hasLetter;
    var layout = { x: 0, y: 0, scale: spec.size != null ? spec.size : (spec.scale || 100), rotation: rot };

    var fs = H * (f.ratio || spec.ratio) * (vertical ? 1.05 : 1);
    if (!vertical) { if (hasLetter) fs *= 0.62; if (hasArt) fs *= 0.6; }
    else if (hasArt || hasLetter) fs *= 0.8;
    var ls = vertical ? Math.min(W * 0.4, H * 0.3) : H * (hasArt ? 0.24 : 0.4);
    var artH = H * (artOnly ? 0.8 : (vertical ? 0.42 : (hasLetter ? 0.38 : 0.46))) * (spec.scale || 100) / 100;
    if (hasArt && spec.size != null) {
      // Size slider 0–100: 100 = as large as fits the printable area, 0 = smallest (20% of that)
      var maxArtH = artOnly ? H * 0.96 : (vertical ? H * 0.9 : H * 0.62);
      var maxArtW = vertical && !artOnly ? W * 0.5 : W * 0.96;
      var fitH = Math.min(maxArtH, maxArtW / artAspect);
      artH = fitH * (0.2 + 0.8 * Math.min(100, Math.max(0, spec.size)) / 100);
    }
    var artW = 0;
    var gap = H * 0.03;

    function setFont(px) {
      x.font = fontString(f, px);
      x.direction = rtl ? 'rtl' : 'ltr';
      // Never letter-space Arabic/Urdu: it breaks the joined letter shapes
      if ('letterSpacing' in x) x.letterSpacing = (rtl ? 0 : f.spacing * px).toFixed(2) + 'px';
    }
    function lineLength(px) {
      if (!hasText) return 0;
      setFont(px);
      return x.measureText(text).width + (hasIcon ? px * 0.62 + px * 0.25 : 0);
    }
    function fitArt() {
      if (!hasArt) return;
      artW = artH * artAspect;
      var maxW = vertical && !artOnly ? W * 0.5 : W * 0.96;
      if (artW > maxW) { artW = maxW; artH = artW / artAspect; }
      if (artH > H * 0.96) { artH = H * 0.96; artW = artH * artAspect; }
    }
    fitArt();
    var maxLen = (vertical ? H : W) * 0.96;
    var len = lineLength(fs);
    if (len > maxLen) { fs *= maxLen / len; len = lineLength(fs); }
    var thick = hasText ? fs * f.thick : 0;

    var gw = 0;
    function graphicsW() { return Math.max(hasArt ? artW : 0, hasLetter ? ls : 0); }
    function graphicsH() { return (hasArt ? artH : 0) + (hasLetter ? ls : 0) + (hasArt && hasLetter ? gap : 0); }
    function total() {
      if (!vertical) return graphicsH() + ((hasArt || hasLetter) && hasText ? gap : 0) + thick;
      gw = graphicsW();
      return gw + (gw && hasText ? gap : 0) + thick;
    }
    var limit = vertical ? W : H;
    for (var i = 0; i < 8 && total() > limit; i++) {
      var k = limit / total();
      if (hasArt && fs > H * 0.08) { fs *= Math.max(0.8, k); ls *= Math.max(0.8, k); }
      else { fs *= k; ls *= k; artH *= k; fitArt(); }
      thick = hasText ? fs * f.thick : 0;
      len = lineLength(fs);
    }
    if (vertical && graphicsH() > H * 0.96) {
      var g = (H * 0.96) / graphicsH();
      ls *= g; artH *= g; fitArt();
    }

    x.fillStyle = '#fff';
    x.strokeStyle = '#fff';

    function drawArt(cx, top) {
      if (!hasArt) return;
      var baseLeft = cx - artW / 2;
      var pos = spec.pos || { x: 0, y: 0 };
      // Customer's offset, kept inside the printable area
      var left = Math.min(W - artW, Math.max(0, baseLeft + (pos.x / 100) * W));
      var t = Math.min(H - artH, Math.max(0, top + (pos.y / 100) * H));
      layout.x = Math.round(((left - baseLeft) / W) * 100);
      layout.y = Math.round(((t - top) / H) * 100);
      // Draw rotated around the centre of its box
      var dw = rot % 180 ? artH : artW;
      var dh = rot % 180 ? artW : artH;
      x.save();
      x.translate(left + artW / 2, t + artH / 2);
      x.rotate((rot * Math.PI) / 180);
      x.drawImage(spec.art.canvas, -dw / 2, -dh / 2, dw, dh);
      x.restore();
    }
    function drawLetter(cx, cy) {
      if (!hasLetter) return;
      var lf = LETTER_FONTS[spec.letterStyle] || FONTS[spec.font] || FONTS.oswald;
      var framed = spec.letterFrame !== 'none';
      var size = framed ? ls * (spec.letterStyle === 'script' ? 0.85 : 0.62) : ls * (spec.letterStyle === 'script' ? 1.2 : 1);
      x.save();
      x.font = fontString(lf, size);
      x.direction = 'ltr';
      if ('letterSpacing' in x) x.letterSpacing = '0px';
      x.textAlign = 'center';
      x.textBaseline = 'middle';
      x.fillText(spec.letter, cx, cy + (spec.letterStyle === 'script' ? size * 0.1 : size * 0.04));
      if (framed) {
        var lw = Math.max(1.5, ls * 0.031);
        x.lineWidth = lw;
        x.beginPath();
        x.arc(cx, cy, ls / 2 - lw / 2, 0, Math.PI * 2);
        x.stroke();
        if (spec.letterFrame === 'double') {
          x.lineWidth = lw * 0.6;
          x.beginPath();
          x.arc(cx, cy, ls / 2 + lw * 1.6, 0, Math.PI * 2);
          x.stroke();
        }
      }
      x.restore();
    }
    function drawLine() { // centred on (0,0) in the current transform
      setFont(fs);
      x.textBaseline = 'middle';
      x.textAlign = 'left';
      var start = -len / 2;
      var is = fs * 0.62;
      var textW = len - (hasIcon ? is + fs * 0.25 : 0);
      // The icon sits where reading starts: left for English, right for Arabic/Urdu
      if (hasIcon && !rtl) {
        x.drawImage(spec.icon.mask, start, -is / 2, is, is);
        start += is + fs * 0.25;
      }
      x.globalAlpha = spec.placeholder ? 0.4 : 1;
      x.fillText(text, start, rtl ? fs * 0.1 : fs * 0.04);
      x.globalAlpha = 1;
      if (hasIcon && rtl) x.drawImage(spec.icon.mask, start + textW + fs * 0.25, -is / 2, is, is);
    }

    if (!vertical) {
      var y = (H - total()) / 2;
      if (hasArt) { drawArt(W / 2, y); y += artH + gap; }
      if (hasLetter) { drawLetter(W / 2, y + ls / 2); y += ls + gap; }
      if (hasText) {
        x.save();
        x.translate(W / 2, y + thick / 2);
        drawLine();
        x.restore();
      }
    } else {
      var tw = total();
      var left = (W - tw) / 2;
      if (gw) {
        var gy = (H - graphicsH()) / 2;
        var gcx = left + gw / 2;
        if (hasArt) { drawArt(gcx, gy); gy += artH + gap; }
        if (hasLetter) drawLetter(gcx, gy + ls / 2);
        left += gw + gap;
      }
      if (hasText) {
        x.save();
        x.translate(left + thick / 2, H / 2);
        x.rotate(-Math.PI / 2); // reads bottom to top
        drawLine();
        x.restore();
      }
    }
    c.layout = layout; // actual (clamped) artwork placement, saved on the order
    return c;
  }

  // Placement guide (never part of the engraving, cart or order images)
  function drawBorder(ctx, w, h) {
    var lw = Math.max(2, Math.round(Math.min(w, h) * 0.006));
    ctx.save();
    ctx.strokeStyle = 'rgba(157, 0, 255, 0.85)';
    ctx.lineWidth = lw;
    ctx.setLineDash([lw * 4, lw * 3]);
    ctx.strokeRect(lw / 2, lw / 2, w - lw, h - lw);
    ctx.restore();
  }

  // Flat view of the printable area: body colour + grey engraving (+ optional guide)
  function renderFlat(target, engraved, bodyHex, border) {
    var t = target.getContext('2d');
    target.width = engraved.width;
    target.height = engraved.height;
    t.fillStyle = bodyHex;
    t.fillRect(0, 0, target.width, target.height);
    t.drawImage(engraved, 0, 0);
    if (border) drawBorder(t, target.width, target.height);
    return target;
  }

  /* ---------- Engraved look ---------- */
  function engrave(mask, colour) {
    var w = mask.width, h = mask.height;
    var s = Math.max(1, Math.round(Math.min(w, h) * 0.004));
    var out = canvas(w, h);
    var o = out.getContext('2d');
    o.drawImage(mask, 0, 0);
    o.globalCompositeOperation = 'source-in';
    o.fillStyle = colour;
    o.fillRect(0, 0, w, h);

    // Inner shadow along the top edges (light from above → recessed cut)
    var shade = canvas(w, h);
    var sx = shade.getContext('2d');
    sx.fillStyle = '#000';
    sx.fillRect(0, 0, w, h);
    sx.globalCompositeOperation = 'destination-out';
    sx.drawImage(mask, 0, s);
    // Soft highlight along the bottom edges
    var lift = canvas(w, h);
    var lx = lift.getContext('2d');
    lx.fillStyle = '#fff';
    lx.fillRect(0, 0, w, h);
    lx.globalCompositeOperation = 'destination-out';
    lx.drawImage(mask, 0, -s);

    o.globalCompositeOperation = 'source-atop';
    if ('filter' in o) o.filter = 'blur(' + Math.max(0.6, s * 0.8) + 'px)';
    o.globalAlpha = 0.5;
    o.drawImage(shade, 0, 0);
    o.globalAlpha = 0.28;
    o.drawImage(lift, 0, 0);
    if ('filter' in o) o.filter = 'none';
    o.globalAlpha = 1;
    o.globalCompositeOperation = 'source-over';
    return out;
  }

  /* ---------- UV mapping ---------- */
  function mapInto(ctx, src, tw, th, map) {
    var x0 = map.uMin * tw;
    var w = (map.uMax - map.uMin) * tw;
    var yTop = (map.flipV ? map.vTop : 1 - map.vTop) * th;
    var yBot = (map.flipV ? map.vBottom : 1 - map.vBottom) * th;
    [0, tw].forEach(function (dx) { // draw twice so the front seam (u = 0/1) wraps cleanly
      ctx.save();
      ctx.translate(x0 + dx, yTop);
      ctx.scale(1, (yBot - yTop) / src.height);
      ctx.drawImage(src, 0, 0, w, src.height);
      ctx.restore();
    });
  }

  function linearToHex(rgb) {
    function ch(c) {
      var v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
      var n = Math.round(Math.min(1, Math.max(0, v)) * 255);
      return (n < 16 ? '0' : '') + n.toString(16);
    }
    return '#' + ch(rgb[0]) + ch(rgb[1]) + ch(rgb[2]);
  }

  // Returns { base, roughMetal } canvases for the body material
  function buildTextures(design, mask, opts) {
    var tw = opts.texW, th = opts.texH;
    var base = canvas(tw, th);
    var b = base.getContext('2d');
    b.fillStyle = opts.bodyHex;
    b.fillRect(0, 0, tw, th);
    var src = design;
    if (opts.border) {
      src = canvas(design.width, design.height);
      var sc = src.getContext('2d');
      sc.drawImage(design, 0, 0);
      drawBorder(sc, src.width, src.height);
    }
    mapInto(b, src, tw, th, opts.map);

    // glTF metallic-roughness: G = roughness, B = metalness.
    // Engraved areas get a subtle satin-metal sheen; the coating stays matte.
    var rw = Math.round(tw / 2), rh = Math.round(th / 2);
    var rm = canvas(rw, rh);
    var r = rm.getContext('2d');
    r.fillStyle = 'rgb(0,' + Math.round(opts.roughness * 255) + ',0)';
    r.fillRect(0, 0, rw, rh);
    var tint = canvas(mask.width, mask.height);
    var t = tint.getContext('2d');
    t.drawImage(mask, 0, 0);
    t.globalCompositeOperation = 'source-in';
    t.fillStyle = 'rgb(0,102,115)';
    t.fillRect(0, 0, tint.width, tint.height);
    mapInto(r, tint, rw, rh, opts.map);
    return { base: base, roughMetal: rm };
  }

  window.VimzoEngraver = {
    FONTS: FONTS,
    SCRIPTS: SCRIPTS,
    textFont: textFont,
    iconMask: iconMask,
    artMask: artMask,
    ensureFonts: ensureFonts,
    composeMask: composeMask,
    renderFlat: renderFlat,
    engrave: engrave,
    buildTextures: buildTextures,
    linearToHex: linearToHex
  };
})();
